import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { ProgressContext } from "./";
import { useVideoPlayerRefs } from "../VideoPlayerRefs";
import { useSeekBar } from "../SeekBar";
import { interpolateByRange } from "../../utils/interpolator";
import { usePointerMove } from "../../hooks/usePointerMove";
import { useRoot } from "../Root";
import {
  TimeUpdateCallback,
  useTimeUpdateEvent,
} from "../../hooks/useTimeUpdateEvent";
import { useClip } from "../Clip";
import { Handler } from "../../types/handler";
import { useRange } from "../Range";

interface Props {
  children: React.ReactNode;
}

export const ProgressProvider = ({ children }: Props) => {
  const { currentTimeOffset, adjustedDuration, range } = useRange();
  const { rangeInTime, rangeInPixels, show: showClipRange } = useClip();
  const { video } = useVideoPlayerRefs();
  const { loop } = useRoot();
  const {
    width: seekBarWidth,
    offset: seekBarOffset,
    activeHandler,
    hoverHandler,
    lastPointerPosition,
  } = useSeekBar();

  const [offset, setOffset] = useState<number>(0);

  const active = activeHandler === Handler.progress;
  const hover = hoverHandler === Handler.progress;

  const moveOffset = useCallback(
    (realCurrentTime: number) => {
      const newOffset =
        seekBarWidth *
        ((realCurrentTime - currentTimeOffset) / adjustedDuration);

      setOffset(newOffset);
    },
    [adjustedDuration, currentTimeOffset, seekBarWidth]
  );

  const handleClipRange = useCallback(() => {
    if (showClipRange && video.current !== null) {
      const { currentTime } = video.current;
      const { start, end } = rangeInTime;

      if (
        (currentTime < start || currentTime > end) &&
        video.current !== null
      ) {
        video.current.currentTime = start + 0.001;
      }
    }
  }, [rangeInTime, showClipRange, video]);

  const handleRange = useCallback(() => {
    if (range !== undefined && video.current !== null) {
      const { start, end } = range;
      const { currentTime } = video.current;

      // If the current time is before the start of the range, jump to the start
      if (currentTime < start) {
        video.current.currentTime = start;
        return;
      }

      // If the current time is after the end of the range and looping is enabled, jump to the start
      if (currentTime > end && loop) {
        video.current.currentTime = start;
        return;
      }

      // If the current time is after the end of the range and looping is disabled, pause the video and dispatch an "ended" event
      if (currentTime >= end) {
        video.current.pause();
        video.current.dispatchEvent(new Event("ended"));
      }
    }
  }, [loop, range, video]);

  useEffect(() => {
    handleRange();
    handleClipRange();
  }, [handleClipRange, handleRange]);

  const moveProgressHandler = useCallback(
    (offset: number) => {
      if (video.current !== null) {
        const ratio = interpolateByRange(0, offset / seekBarWidth, 1);
        const newCurrentTime = adjustedDuration * ratio + currentTimeOffset;

        moveOffset(newCurrentTime);
        video.current.currentTime = newCurrentTime;
      }
    },
    [video, seekBarWidth, adjustedDuration, currentTimeOffset, moveOffset]
  );

  const moveHandler = useCallback(
    (e: PointerEvent) => {
      const newOffset = interpolateByRange(
        showClipRange ? rangeInPixels.start : 0,
        e.x - seekBarOffset,
        showClipRange ? rangeInPixels.end : seekBarWidth
      );

      moveProgressHandler(newOffset);
    },
    [
      moveProgressHandler,
      rangeInPixels.end,
      rangeInPixels.start,
      seekBarOffset,
      seekBarWidth,
      showClipRange,
    ]
  );

  usePointerMove(activeHandler === Handler.progress, moveHandler);

  useLayoutEffect(() => {
    if (
      activeHandler === Handler.progress &&
      lastPointerPosition !== undefined
    ) {
      moveProgressHandler(lastPointerPosition);
    }
  }, [activeHandler, lastPointerPosition, moveProgressHandler]);

  /**
   * TIME UPDATE HANDLER
   */
  const timeUpdateHandler: TimeUpdateCallback = useCallback(
    ({ currentTime }) => {
      handleRange();
      handleClipRange();
      moveOffset(currentTime);
    },
    [handleClipRange, handleRange, moveOffset]
  );

  useTimeUpdateEvent(true, timeUpdateHandler);

  const value = useMemo(
    () => ({ offset, hover, active }),
    [active, hover, offset]
  );

  return (
    <ProgressContext.Provider value={value}>
      {children}
    </ProgressContext.Provider>
  );
};
